import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#submenuPanels = this.querySelector('.header-menu__panels');
    this.addEventListener('pointermove', this.#trackPointerPosition, { passive: true });
    this.addEventListener('pointerleave', this.#onHeaderPointerLeave);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    this.removeEventListener('pointerleave', this.#onHeaderPointerLeave);
    this.removeEventListener('pointermove', this.#trackPointerPosition);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#deactivateTimer);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  #trackPointerPosition = (event) => {
    if (!(event instanceof PointerEvent)) return;
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
  };

  #onHeaderPointerLeave = (event) => {
    if (!(event instanceof PointerEvent)) return;
    const related = event.relatedTarget;
    if (related instanceof Node && this.contains(related)) return;
    this.#deactivate();
  };

  #onDocumentPointerDown = (event) => {
    if (!this.#state.activeItem || !(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  #clearAllSubmenuStates() {
    this.querySelectorAll('.header-menu__panels [ref="submenu[]"]').forEach((submenu) => {
      delete submenu.dataset.active;
      submenu.style.removeProperty('--dropdown-submenu-left');
    });
  }

  #isPointerInMenuZone() {
    if (!this.#state.activeItem) return false;

    const { x, y } = this.#lastPointer;
    const target = document.elementFromPoint(x, y);
    if (!(target instanceof Node)) return false;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (activeSubmenu?.contains(target)) return true;

    const activeListItem = this.#state.activeItem.closest('.menu-list__list-item');
    if (activeListItem?.contains(target)) return true;

    return false;
  }

  #scheduleDeactivate() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      if (this.#isPointerInMenuZone()) return;
      this.#deactivate();
    }, 120);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * @type {HTMLElement | null}
   */
  #submenuPanels = null;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu?.contains(target)) return;

      const listItem = target.closest('.menu-list__list-item');
      const activeListItem = this.#state.activeItem?.closest('.menu-list__list-item');
      if (listItem && listItem !== activeListItem) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    clearTimeout(this.#deactivateTimer);
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#clearAllSubmenuStates();

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const isDropdownSubmenu = submenu?.classList.contains('menu-list__submenu--dropdown');

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      if (isDropdownSubmenu && submenu.closest('.header-menu__panels')) {
        const linkEl = item.matches('[ref="menuitem"]') ? item : item.querySelector('[ref="menuitem"]');
        const itemRect = (linkEl ?? item).getBoundingClientRect();
        const menuRect = this.getBoundingClientRect();
        submenu.style.setProperty('--dropdown-submenu-left', `${itemRect.left - menuRect.left}px`);
      }

      if (!isDropdownSubmenu) {
        // Cleanup any existing mutation observer from previous menu activations
        this.#cleanupMutationObserver();

        // Monitor DOM mutations to catch deferred content injection (from section hydration)
        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        // Auto-disconnect after 500ms to prevent memory leaks
        setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }
    }

    let finalHeight = isDropdownSubmenu ? 0 : submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot && !isDropdownSubmenu) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    const pointerItem = item.closest('.menu-list__list-item') ?? item;
    this.#startPointerTracking(pointerItem, previouslyActiveItem?.closest?.('.menu-list__list-item') ?? previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const related = event.relatedTarget;
    const isMovingWithinMenu = related instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu = related instanceof Node && menu?.contains(related);
    const isMovingToOverflowMenu =
      related instanceof Node && related.parentElement?.matches('[slot="overflow"]');
    const isMovingToPanels =
      related instanceof Node &&
      this.#submenuPanels?.contains(related) &&
      findSubmenu(this.#state.activeItem)?.contains(related);

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingToPanels) {
      clearTimeout(this.#deactivateTimer);
      return;
    }

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    clearTimeout(this.#deactivateTimer);

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    this.#clearAllSubmenuStates();
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) return element;

  const listItem = element.closest('.menu-list__list-item');
  const listItemLink = listItem?.querySelector('[ref="menuitem"]');
  if (listItemLink instanceof HTMLElement) return listItemLink;

  const submenu = element.closest('[ref="submenu[]"]');
  if (submenu) {
    const submenuId = submenu.querySelector('.menu-list__submenu-inner[id]')?.id;
    const headerMenu = submenu.closest('header-menu');
    if (submenuId && headerMenu) {
      const link = headerMenu.querySelector(`[aria-controls="${submenuId}"]`);
      if (link instanceof HTMLElement) return link;
    }
  }

  const menuitem = element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const headerMenu = element.closest('header-menu');
  const menuitem =
    element.matches('[ref="menuitem"]') ? element : element.querySelector?.('[ref="menuitem"]');
  const controlsId = menuitem?.getAttribute('aria-controls');

  if (controlsId && headerMenu) {
    const submenuInner = headerMenu.querySelector(`#${CSS.escape(controlsId)}`);
    const panelSubmenu = submenuInner?.closest('[ref="submenu[]"]');
    if (panelSubmenu instanceof HTMLElement) return panelSubmenu;
  }

  const listItem = element.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
